import type { ProfanityLevel } from "../state";

/**
 * The phrase bank. Everything the app says on the countdown screen and in
 * notifications is assembled from the lists in this file (plus the themed
 * packs in packs.ts). Adding a line here is all it takes to widen the
 * rotation. See docs/COPY.md for the full seeding guide, and run
 * `npm run copy` to lint the bank and print sample days.
 *
 * Three tiers, keyed by ProfanityLevel: 0 Polite (childish British
 * mock-swearing, no real profanity), 1 Spicy (real swearing, moderate),
 * 2 F**ked (maximum feral). Real anatomical/scatological profanity is fine
 * in the top two tiers; nothing that targets a protected group.
 *
 * ---------------------------------------------------------------------
 * Template grammar (used by PUNCHLINES, SUBLINES, HEADLINES, NOTIFICATIONS)
 *
 *   {curse}     a word from CURSES        "sodding"
 *   {pnoun}     a word from PUNCHLINE_NOUNS  "bollocks"
 *   {noun}      a phrase from NOUNS       "right pain in the arse"
 *   {ending}    a phrase from ENDINGS     "to wade through"
 *   {simile}    a phrase from SIMILES     "wasp in a phone box"
 *   {a:simile}  same, with "a"/"an" put in front automatically
 *   {event}     the event name            "Stag Do"
 *   {person}    the person's name         "Dan"
 *   {,person}   ", Dan", or nothing when no name was given
 *   {days}      whole days left           "43"
 *   {s}         "s" unless {days} is 1    "day{s}" -> "day" / "days"
 *
 * The same slot used twice in one line never repeats a word, and a line
 * never uses the same swear root twice ("fucking ... fuck"): the engine
 * re-rolls until it's clean.
 *
 * A template can be a plain string, or [template, weight] to make it come
 * up more (or less) often. Default weight is 1.
 * ---------------------------------------------------------------------
 */

export type Tiered<T> = Record<ProfanityLevel, T[]>;
export type Weighted = string | [template: string, weight: number];

/**
 * How far out the event is. The engine works this out from days left, so
 * copy can change tone as the day gets closer.
 *   far      more than 60 days
 *   mid      8 to 60 days
 *   close    2 to 7 days
 *   tomorrow 1 day
 *   today    under a day, not yet reached
 */
export type Phase = "far" | "mid" | "close" | "tomorrow" | "today";
export type Phased<T> = Record<Phase, T>;

// ---------- words ----------

/** Adjective-ish swears. Must read naturally in "of {curse}, {curse}, {curse} {pnoun}". */
export const CURSES: Tiered<string> = {
  0: [
    "blinking", "flipping", "flaming", "blimming", "ruddy", "jolly", "hecking", "pesky", "cheeky", "bothersome",
    "cor-blimey", "gordon-bennett", "utterly rotten", "frightfully naughty", "diabolically cheeky",
    "monstrously silly", "criminally daft", "scandalously soggy",
  ],
  1: [
    "bloody", "sodding", "fucking", "shitting", "bollocking", "bleeding", "arse-clenching", "goddamn",
    "piss-taking", "knackering", "bastard", "buggering", "chuffing", "gormless",
  ],
  2: [
    "cunting", "fucking", "bollocksing", "shitting", "arse-fucking", "twatting", "motherfucking",
    "bell-ending", "knobbing", "shit-for-brains", "cock-wombling", "shit-gargling", "cunt-waffling",
    "knob-goblining", "arse-trumpeting", "bollock-chundering", "twat-waffling", "feral-humping",
    "knacker-knocking", "fuckery-forging",
  ],
};

/** Short, single-word "problem" noun for the punchline: "... sodding {pnoun} to wade through". */
export const PUNCHLINE_NOUNS: Tiered<string> = {
  0: ["faff", "palaver", "nonsense", "kerfuffle", "malarkey", "carry-on", "hoo-ha", "to-do"],
  1: ["shit", "bollocks", "bullshit", "mess", "nonsense", "piss-take"],
  2: ["shit", "fuck", "bollocks", "arse", "piss", "bell-end", "cunt", "twat", "knob", "carnage", "chaos", "havoc"],
};

/** Longer noun phrases, mostly for notifications: "Bit of {noun} yet before {event}." */
export const NOUNS: Tiered<string> = {
  0: [
    "faff", "palaver", "kerfuffle", "malarkey", "carry-on", "to-do", "song and dance", "hoo-ha", "rigmarole",
    "shemozzle", "tommyrot", "flim-flam", "hullabaloo", "brouhaha", "how's-your-father", "biscuit-tin drama",
    "school-fete shambles", "right two-and-eight",
  ],
  1: [
    "bollocks", "shit", "bullshit", "piss-take", "dog's dinner", "utter shambles", "right pain in the arse",
    "absolute nightmare", "bloody carry-on", "sodding mess", "complete cock-up", "utter bollocks",
  ],
  2: [
    "cunt of a wait", "absolute bollocks", "utter shitshow", "feral bloody nightmare", "unholy bollocking",
    "bell-end of a slog", "knobhead of an ordeal", "twatting nightmare", "right tits-up disaster",
    "proper cunt of a stretch", "unholy shitstorm", "bollock-dropping catastrophe", "feral car-crash of a wait",
    "grade-A bollocking nightmare", "utter dumpster fire", "five-alarm cunt of a mess",
  ],
};

/** "to ..." verb phrases that finish the punchline. Always start with "to ". */
export const ENDINGS: Tiered<string> = {
  0: ["to get through", "to sit through", "to wade through", "to put up with", "to plough through", "to muddle through"],
  1: ["to wade through", "to deal with", "to get through", "to put up with", "to slog through", "to wrestle with"],
  2: [
    "to wade through", "to deal with", "to slog through", "to wrestle with", "to stomach", "to swim through",
    "to plough through like a bull in a china shop", "to neck back like flat cider",
    "to churn through like a woodchipper", "to bulldoze through like a wrecking ball",
    "to gurgle down like a dodgy kebab", "to detonate our way through",
  ],
};

/** Things it's "about as fun as". No leading "a"/"an": the engine adds the right one. */
export const SIMILES: Tiered<string> = {
  0: [
    "wasp at a picnic", "soggy biscuit", "puncture on the school run", "flat lemonade at a birthday do",
    "sock full of gravel", "queue for the loo at half-time", "washing line in the rain",
    "deckchair with a dodgy leg", "sports day in wellies",
  ],
  1: [
    "fart in a lift", "wasp in a phone box", "wet dog at a wedding", "flat tyre on the motorway",
    "traffic warden on a stag do", "kazoo at a funeral", "puncture in the rain",
    "broken deckchair on holiday", "queue at the tip on a bank holiday",
  ],
  2: [
    "fart in a spacesuit", "wasp in a bra", "fist fight in a phone box", "wet fart at a funeral",
    "traffic warden's wet dream", "kazoo solo at a wake", "shart on a first date",
    "greased weasel down your trousers", "conga line of angry wasps",
    "bare-knuckle boxing match in a bouncy castle", "food fight at a funeral",
    "fireworks display in a phone box", "buffet at a wasp convention", "kebab-shop punch-up at 3am",
    "conga line through a minefield", "streaker at a christening", "bin lorry reversing into a wedding cake",
    "milk float doing wheelies down the motorway",
  ],
};

// ---------- sentence shapes ----------

/**
 * The punchline card. On screen it follows the seconds count, so every
 * shape has to read as a continuation of "...exactly 3,733,199 seconds / Of ___".
 * The view adds "of " (lower case, reading on from the numbers above), so these start lower-case, mid-sentence.
 */
export const PUNCHLINES: Tiered<Weighted> = {
  0: [
    ["{curse}, {curse}, {curse} {pnoun} {ending}.", 4],
    "{curse} {pnoun} {ending} before {event}.",
    "{curse}, {curse} {pnoun}, and not one second of it optional.",
    "pure, uncut {curse} {pnoun} {ending}.",
  ],
  1: [
    ["{curse}, {curse}, {curse} {pnoun} {ending}.", 4],
    "{curse} {pnoun} {ending} before {event}.",
    "{curse}, {curse} {pnoun}, and not one second of it optional.",
    "pure, uncut {curse} {pnoun} {ending}.",
    "{curse} {pnoun}. Every. Single. One.",
  ],
  2: [
    ["{curse}, {curse}, {curse} {pnoun} {ending}.", 4],
    "{curse} {pnoun} {ending} before {event}.",
    "{curse}, {curse} {pnoun}, and not one second of it optional.",
    "pure, uncut {curse} {pnoun} {ending}.",
    "{curse} {pnoun}. Every. Single. One.",
    "{curse}, {curse} {pnoun} {ending}, and that's being generous.",
  ],
};

/**
 * The smaller second line on the punchline card. Varies by phase, so the
 * tone shifts as the day gets closer. The same shapes serve every tier;
 * the words dropped into them are what change.
 */
export const SUBLINES: Phased<Weighted[]> = {
  far: [
    ["About as fun as {a:simile}.", 3],
    "Roughly as enjoyable as {a:simile}.",
    "Long way to go. Like {a:simile}, but longer.",
  ],
  mid: [["About as fun as {a:simile}.", 3], "Roughly as enjoyable as {a:simile}.", "Every day of it like {a:simile}."],
  close: [
    "Nearly there. Still about as fun as {a:simile}.",
    "Final stretch. Still {a:simile} out here.",
    "Under a week. Keep it together.",
  ],
  tomorrow: ["One more sleep. One more {curse} {pnoun}.", "Tomorrow. Sleep tight, if you can."],
  today: ["Today's the day. Last lap of {curse} {pnoun}.", "It's today. Hours, not days."],
};

/**
 * The greeting above the numbers. Deliberately upbeat, never vulgar: the
 * punchline is where the complaining happens. Cycled so none repeats until
 * the whole list has been shown, and never the same one two days running.
 * "mid" doubles as the everyday list; "far" adds to it rather than
 * replacing it.
 */
export const HEADLINES: Phased<string[]> = {
  far: ["Long game{,person}. We've got this.", "Plenty of time{,person}. Pace yourself."],
  mid: [
    "Right then{,person}. Let's crack on.",
    "Morning{,person}. Another day closer.",
    "Well, look at that{,person}. Closer than yesterday.",
    "Hello{,person}. Onward we go.",
    "Steady as she goes{,person}.",
    "One day nearer{,person}.",
    "Still counting{,person}. Still worth it.",
    "Good to see you{,person}. Carry on.",
  ],
  close: ["Final week{,person}. Look at you.", "So close{,person}.", "Home straight{,person}."],
  tomorrow: ["Tomorrow{,person}. Tomorrow!", "Last full day{,person}."],
  today: ["It's today{,person}. It's actually today.", "Big day{,person}. Nearly time."],
};

/**
 * The line under a push notification. The title already says how many
 * days are left and what for ("30 days till Stag Do"), so these don't
 * repeat either: one short, rude line that reads well on a lock screen.
 * Keep them under about 45 characters. Notifications always use the
 * Spicy tier (the user's call, 2026-09-23); the others are kept so the
 * copy lab still covers them if that changes.
 */
export const NOTIFICATIONS: Tiered<Weighted> = {
  0: [
    "One {curse} day closer.",
    "Another {curse} day down.",
    "Still {curse} waiting. Chin up{,person}.",
  ],
  1: [
    ["One {curse} day closer.", 2],
    ["Another {curse} day down.", 2],
    "Every {curse} sleep counts.",
    "Not long now. Well, {curse} long.",
    "Still {curse} waiting. Hang in there{,person}.",
    "Keep {curse} going{,person}.",
  ],
  2: [
    "One {curse} day closer, ya knob.",
    "Another {curse} day down, ya cunt.",
    "Still {curse} waiting. Hang in there{,person}.",
  ],
};

// ---------- guard rails ----------

/**
 * Swear roots used to stop one line doubling up ("cunting ... cunt of a
 * wait"). When you add a new swear that isn't built on one of these, add
 * its root here too; `npm run copy` warns when a bank word looks sweary
 * but isn't covered.
 */
export const SWEAR_ROOTS = [
  "cunt", "fuck", "twat", "bollock", "bell-end", "knob", "shit", "arse", "tit", "bastard", "feral", "piss",
  "cock", "knacker", "bugger", "wank",
];

/**
 * Words the censor masks when "Censor swearing" is on. Matched as the start
 * of a word (after an optional "mother"), so "fuckery" and "motherfucking"
 * are caught too. CENSOR_ALLOW lists innocent words that happen to start
 * with one of these.
 */
export const CENSOR_ROOTS = [
  "fuck", "cunt", "shit", "bastard", "bollock", "bugger", "twat", "bellend", "knob", "dickhead", "cock", "arse",
  "piss", "knacker", "wank", "tits", "bullshit",
];
export const CENSOR_ALLOW = [
  "cocktail", "cockney", "cockerel", "cockpit", "cockle", "cockroach", "arsenal", "arsenic", "shitake",
  "knobbly", "pissarro",
];
